import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getIsAdmin } from "@/lib/payments.functions";
import {
  scanSecurity,
  previewSecurityAutofix,
  applySecurityAutofix,
  getAutofixAuditLog,
  rollbackAutofix,
  type AutofixScan,
  type AutofixAuditRow,
} from "@/lib/security-autofix.functions";

import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/security-autofix")({
  head: () => ({ meta: [{ title: "Security Auto-Fix — Admin" }] }),
  component: AdminSecurityAutofixPage,
});

function AdminSecurityAutofixPage() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const doScan = useServerFn(scanSecurity);
  const doPreview = useServerFn(previewSecurityAutofix);
  const doApply = useServerFn(applySecurityAutofix);
  const doLog = useServerFn(getAutofixAuditLog);
  const doRollback = useServerFn(rollbackAutofix);

  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState<AutofixScan | null>(null);
  const [planned, setPlanned] = useState<string[] | null>(null);
  const [applying, setApplying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [audit, setAudit] = useState<AutofixAuditRow[]>([]);
  const [rollingBack, setRollingBack] = useState<string | null>(null);


  useEffect(() => {
    (async () => {
      const { isAdmin } = await checkAdmin();
      if (!isAdmin) {
        navigate({ to: "/" });
        return;
      }
      await Promise.all([runScan(), refreshAudit()]);
    })();
  }, []);

  const runScan = async () => {
    setLoading(true);
    const r = await doScan();
    if ("error" in r) toast.error(r.error);
    else setScan(r.scan);
    setLoading(false);
  };

  const refreshAudit = async () => {
    const r = await doLog();
    if (!("error" in r)) setAudit(r.rows);
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPlanned(null);
    const r = await doPreview();
    setPreviewing(false);
    if ("error" in r) {
      toast.error(r.error);
      return;
    }
    setPlanned(r.preview.planned ?? []);
    toast.success(`Dry-run: ${r.preview.count} statement(s) would run.`);
    refreshAudit();
  };

  const runApply = async () => {
    if (!planned || planned.length === 0) {
      toast.error("Run dry-run first.");
      return;
    }
    if (!confirm(`Apply ${planned.length} SQL statement(s) shown in the dry-run preview?`)) return;
    setApplying(true);
    const r = await doApply();
    setApplying(false);
    if ("error" in r) {
      toast.error(r.error);
      return;
    }
    toast.success(`Applied ${r.result.count ?? 0} fix(es).`);
    setPlanned(null);
    await Promise.all([runScan(), refreshAudit()]);
  };
  const runRollback = async (row: AutofixAuditRow) => {
    if (row.action !== "apply" || row.rolled_back_at || !row.reverse_sql?.length) return;
    if (!confirm(`Roll back this apply run?\n\nThis will execute ${row.reverse_sql.length} reverse SQL statement(s):\n\n${row.reverse_sql.join("\n")}`)) return;
    setRollingBack(row.id);
    const r = await doRollback({ data: { applyId: row.id } });
    setRollingBack(null);
    if ("error" in r) {
      toast.error(r.error);
      return;
    }
    toast.success(`Rolled back ${r.result.count} statement(s).`);
    await Promise.all([runScan(), refreshAudit()]);
  };


  const totalIssues =
    (scan?.tables_missing_rls.length ?? 0) +
    (scan?.definer_functions_missing_search_path.length ?? 0) +
    (scan?.definer_functions_public_executable.length ?? 0);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Security Auto-Fix</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Scan for known-safe issues, preview the exact SQL, then apply. Every action is recorded in the audit log below.
          </p>
        </header>

        <div className="flex flex-wrap gap-2">
          <Button onClick={runScan} disabled={loading} variant="outline">
            {loading ? "Scanning…" : "Re-scan"}
          </Button>
          <Button onClick={runPreview} disabled={previewing || totalIssues === 0} variant="secondary">
            {previewing ? "Preparing…" : `Dry-run (preview SQL)`}
          </Button>
          <Button onClick={runApply} disabled={applying || !planned || planned.length === 0}>
            {applying ? "Applying…" : `Apply ${planned?.length ?? 0} statement(s)`}
          </Button>
        </div>

        {scan && (
          <div className="space-y-6">
            <Section title="Tables missing RLS" items={scan.tables_missing_rls.map((t) => t.table_name)} />
            <Section
              title="SECURITY DEFINER functions missing search_path"
              items={scan.definer_functions_missing_search_path.map((f) => `${f.function_name}(${f.args})`)}
            />
            <Section
              title="SECURITY DEFINER functions executable by anon/public"
              items={scan.definer_functions_public_executable.map((f) => `${f.function_name}(${f.args}) → ${f.granted_to}`)}
            />
          </div>
        )}

        {planned && (
          <div className="rounded-md border p-4 bg-muted/40">
            <h2 className="font-medium mb-2">Dry-run SQL preview ({planned.length})</h2>
            {planned.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing to do.</p>
            ) : (
              <pre className="text-xs whitespace-pre-wrap font-mono">{planned.join("\n")}</pre>
            )}
          </div>
        )}

        <section className="rounded-md border p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium">Audit log (last 50)</h2>
            <Button onClick={refreshAudit} variant="ghost" size="sm">Refresh</Button>
          </div>
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground">No entries yet.</p>
          ) : (
            <ul className="text-sm space-y-2">
              {audit.map((row) => {
                const canRollback = row.action === "apply" && !row.rolled_back_at && !row.rollback_of && !!row.reverse_sql?.length;
                return (
                  <li key={row.id} className="border-b pb-2 last:border-b-0">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <span className="font-mono uppercase">{row.action}</span>
                        {row.rollback_of && <span className="ml-2 text-xs rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">rollback</span>}
                        {row.rolled_back_at && <span className="ml-2 text-xs rounded bg-muted px-1.5 py-0.5">reverted</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">{new Date(row.created_at).toLocaleString()}</span>
                        {canRollback && (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={rollingBack === row.id}
                            onClick={() => runRollback(row)}
                          >
                            {rollingBack === row.id ? "Rolling back…" : "Roll back"}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      actor: {row.actor_user_id ?? "system"}
                      {row.finding_counts ? ` • ${JSON.stringify(row.finding_counts)}` : ""}
                      {row.applied_sql?.length ? ` • applied ${row.applied_sql.length}` : ""}
                      {row.planned_sql?.length ? ` • planned ${row.planned_sql.length}` : ""}
                      {row.reverse_sql?.length ? ` • reversible ${row.reverse_sql.length}` : ""}
                    </div>
                  </li>
                );
              })}

            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function Section({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="rounded-md border p-4">
      <h2 className="font-medium mb-2">
        {title} <span className="text-muted-foreground">({items.length})</span>
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Clean.</p>
      ) : (
        <ul className="text-sm space-y-1">
          {items.map((i) => (
            <li key={i} className="font-mono">{i}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
